import L, { PointExpression } from 'leaflet'
import { CitiesProps, JsonAirport } from './types'
import { calculateIconSize, getAirports } from './utils'
export class FlightViewMapLibCities {
  readonly #props: CitiesProps

  readonly #map: L.Map
  readonly #customIcon: L.Icon
  #airports: JsonAirport[] = []
  #iconSize: number = 10
  readonly #citiesLayer: L.FeatureGroup
  readonly #mainAirport: string
  readonly #cityLabel: string
  readonly #markers: L.Marker[] = []

  constructor (map: L.Map, initProps: CitiesProps, customIcon?: L.Icon<any>) {
    this.#props = initProps
    this.#map = map
    this.#customIcon = customIcon
    this.#cityLabel = this.#props.cityLabel || 'name'
    this.#citiesLayer = new L.FeatureGroup().addTo(this.#map)
    this.#iconSize = calculateIconSize(this.#props.airportIconSize, this.#iconSize)
    if (typeof this.#props.airport === 'string') {
      this.#mainAirport = this.#props.airport
    } else {
      this.#mainAirport = this.#props.airport?.code
    }
  }

  removeLayer () {
    this.#map.removeLayer(this.#citiesLayer)
  }

  addLayer () {
    this.#map.addLayer(this.#citiesLayer)
  }

  getBounds () {
    return this.#citiesLayer.getBounds()
  }

  async initialize () {
    this.#airports = await getAirports()
    const customerAirport = this.#airports.find((airport) => airport.code === this.#mainAirport)
    if (customerAirport) {
      const cityName = this.#cityLabel === 'name' ? customerAirport.label : customerAirport.code
      if (typeof this.#props.airport === 'string' || this.#props.airport instanceof String) {
        this.#addAirportMarker(customerAirport, null, cityName)
      } else {
        this.#addAirportMarker(customerAirport, null, this.#props.airport.label || cityName, this.#props.airport.direction)
      }
    }
    this.#setAirportMarkers()
  }

  getMarkerElement (airportCode: string): Element {
    const marker = this.#markers.find((marker) => marker.code === airportCode)
    if (marker) {
      return marker.getElement()
    } else {
      return null
    }
  }

  #setAirportMarkers () {
    const mainAirport = this.#airports.find((airport) => airport.code === this.#mainAirport)
    for (const city of this.#props.cities) {
      if (typeof city === 'string' || city instanceof String) {
        if (city === this.#mainAirport) continue
        const airport = this.#airports.find((airport) => airport.code === city)
        if (airport) {
          this.#addAirportMarker(airport, mainAirport, this.#cityLabel === 'name' ? airport.label : airport.code)
        }
      } else {
        if (city.code === this.#mainAirport) continue
        const airport = this.#airports.find((airport) => airport.code === city.code)
        if (airport) {
          const cityName = this.#cityLabel === 'name' ? airport.label : airport.code
          this.#addAirportMarker(airport, mainAirport, city.label || cityName, city.direction)
        }
      }
    }
  }

  #addAirportMarker (airport: JsonAirport, mainAirport: JsonAirport, customLabel?: string, direction?: L.Direction) {
    let adjustedLng = airport.lng
    if (mainAirport && airport.code !== mainAirport.code) {
      adjustedLng += airport.lng - mainAirport.lng > 180 ? -360 : mainAirport.lng - airport.lng > 180 ? 360 : 0
    }
    let marker
    if (this.#customIcon) {
      marker = new L.Marker(new L.LatLng(airport.lat, adjustedLng), { icon: this.#customIcon })
    } else {
      marker = new L.CircleMarker(new L.LatLng(airport.lat, adjustedLng), {
        radius: this.#iconSize / 2,
        fill: true,
        className: 'leaflet-airport-marker'
      })
    }
    marker.bindTooltip(customLabel || airport.label, {
      className: 'leaflet-airport-tooltip',
      pane: 'city-names',
      permanent: true,
      direction: direction || 'auto',
      offset: this.#calculateOffset(direction)
    })
    marker.code = airport.code
    marker.addTo(this.#citiesLayer)
    this.#markers.push(marker)
  }

  #calculateOffset (direction: L.Direction): PointExpression {
    if (direction === 'left') {
      return [-this.#iconSize / 2, 0]
    } else if (direction === 'right') {
      return [this.#iconSize / 2, 0]
    } else if (direction === 'top') {
      return [0, -this.#iconSize / 2]
    } else if (direction === 'bottom') {
      return [0, this.#iconSize / 2]
    } else {
      return [0, 0]
    }
  }
}
